import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Analysis {
  _id: string;
  title: string;
  description: string;
  totalEmployees: number;
  employeesWithViolations: number;
  totalTheftHours: number;
  estimatedLoss: number;
  status: string;
  createdAt: string;
}

interface Employee {
  _id: string;
  name: string;
  totalTheft: string;
  complianceRate: number;
  timeTheftWeeks: number;
  totalWeeks: number;
  severity: string;
  weeklyData: any[];
}

interface AnalysisState {
  analyses: Analysis[];
  currentAnalysis: Analysis | null;
  employees: Employee[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const initialState: AnalysisState = {
  analyses: [],
  currentAnalysis: null,
  employees: [],
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
};

const analysisSlice = createSlice({
  name: 'analysis',
  initialState,
  reducers: {
    fetchAnalysesRequest: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchAnalysesSuccess: (state, action: PayloadAction<{ analyses: Analysis[]; pagination: any }>) => {
      state.analyses = action.payload.analyses;
      state.pagination = action.payload.pagination;
      state.loading = false;
    },
    fetchAnalysesFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchAnalysisDetailRequest: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    fetchAnalysisDetailSuccess: (state, action: PayloadAction<{ analysis: Analysis; employees: Employee[] }>) => {
      state.currentAnalysis = action.payload.analysis;
      state.employees = action.payload.employees;
      state.loading = false;
    },
    fetchAnalysisDetailFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    deleteAnalysisRequest: (state, action: PayloadAction<string>) => {
      state.loading = true;
    },
    deleteAnalysisSuccess: (state, action: PayloadAction<string>) => {
      state.analyses = state.analyses.filter(a => a._id !== action.payload);
      state.loading = false;
    },
    deleteAnalysisFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    clearCurrentAnalysis: (state) => {
      state.currentAnalysis = null;
      state.employees = [];
    },
  },
});

export const {
  fetchAnalysesRequest,
  fetchAnalysesSuccess,
  fetchAnalysesFailure,
  fetchAnalysisDetailRequest,
  fetchAnalysisDetailSuccess,
  fetchAnalysisDetailFailure,
  deleteAnalysisRequest,
  deleteAnalysisSuccess,
  deleteAnalysisFailure,
  clearCurrentAnalysis,
} = analysisSlice.actions;

export default analysisSlice.reducer;